import { useEffect, useState } from 'react';

import { Card, Text } from '@gympass/yoga';
import { useQuery } from '@tanstack/react-query';
import withAuth from 'hocs/withAuth/withAuth';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useRequest } from 'hooks';
import { useLogger } from 'hooks/useLogger/useLogger';
import {
  EndUserState,
  getEndUserInfo,
  PreAssessmentQuestions,
  getEndUserPreAssessmentsV1,
  getEndUserPreAssessmentsV2,
  PreAssessmentResult,
} from 'services/user';

import { HeaderUserInfo, Loading, Paginator, TabList } from '../../components';

import {
  PatientFileAnswer,
  PreAssessmentAnswerV1,
  PreAssessmentAnswerV2,
} from './types';

import * as S from './styles';

const PreAssessment: React.FC = () => {
  const [userInfo, setUserInfo] = useState<EndUserState>({} as EndUserState);
  const [patientFileAnswers, setPatientFileAnswers] =
    useState<PatientFileAnswer | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [preAssessment, setPreAssessment] = useState<
    PreAssessmentAnswerV2[] | PreAssessmentAnswerV1[]
  >([]);

  const [paginatedPreAssessment, setPaginatedPreAssessment] = useState<
    PreAssessmentAnswerV2[] | PreAssessmentAnswerV1[]
  >([]);
  const getUserInfoRequest = useRequest();
  const workflowId = 'onboarding';
  const { id: cognitoId = '' } = useParams<{
    id: string;
  }>();
  const intl = useIntl();
  const logger = useLogger();
  const { isSuccess: userInfoIsSuccess } = useQuery(
    ['getEndUserInfo', cognitoId],
    () => getEndUserInfo(cognitoId),
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      onSuccess: (data: { data: EndUserState }) => {
        const user = data.data;
        if (user.patient_intake.answer) {
          const patientFileAnswersJson = JSON.parse(user.patient_intake.answer);
          setPatientFileAnswers(patientFileAnswersJson);
        }
        setUserInfo(user);
      },
      onError: e => {
        logger.error('e', { e });
      },
    },
  );

  const { refetch: preAssessmentsV1Refetch } = useQuery(
    ['clinicalEndUserAssessmentsV1', cognitoId],
    () => getEndUserPreAssessmentsV1(cognitoId),
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: (result: PreAssessmentResult) => {
        const { fields } = result.listSubmissions.entries[0];
        const preAssessmentsList = fields
          .filter(field => field.field.title)
          .map((item, idx) => {
            const { value } = JSON.parse(item.value);

            const { title, description } = item.field;
            const optionsValue = Array.isArray(value) ? value : [value];
            const optionsTitle = optionsValue.map(optionItem => {
              const foundOption = item.field.options.find(curOption => {
                return curOption.value === optionItem;
              });

              return foundOption?.title;
            });

            return {
              id: idx,
              title,
              description,
              answer: optionsTitle.join(', '),
            };
          });

        setTotalPages(result.listSubmissions.entries.length);
        setPreAssessment(preAssessmentsList);
      },
      onError: e => {
        logger.error('e', { e });
      },
    },
  );

  const { refetch: preAssessmentsV2Refetch } = useQuery(
    ['clinicalEndUserAssessments', cognitoId, workflowId],
    () => getEndUserPreAssessmentsV2(cognitoId, workflowId),
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: (data: PreAssessmentQuestions) => {
        setTotalPages(data.questions.length);
        setPreAssessment(data.questions);
      },
      onError: e => {
        logger.error('e', { e });
      },
    },
  );

  const paginatePreAssessments = () => {
    const pageSize = 5;
    const paginated = preAssessment.slice(
      (pageNumber - 1) * pageSize,
      pageNumber * pageSize,
    );

    const pagesCount =
      preAssessment.length === 0
        ? 1
        : Math.ceil(preAssessment.length / pageSize);

    setPaginatedPreAssessment(paginated);
    setTotalPages(pagesCount);
  };

  const handleNextPage = () => {
    if (pageNumber === totalPages) return;
    setPageNumber(pageNumber + 1);
  };

  const handlePreviousPage = () => {
    if (pageNumber === 1) return;
    setPageNumber(pageNumber - 1);
  };

  const listPreassessmentV1 = (preasessment: PreAssessmentAnswerV1[]) => {
    return (
      <S.RecordContent data-testid="preassessmentContent">
        {preasessment &&
          preasessment.map(item => (
            <S.Record key={item.id}>
              <S.RecordTitle>{item.title}</S.RecordTitle>
              <S.RecordTitle>{item.description}</S.RecordTitle>
              <p>{item.answer}</p>
            </S.Record>
          ))}
      </S.RecordContent>
    );
  };

  const listPreassessmentV2 = (preasessment: PreAssessmentAnswerV2[]) => {
    return (
      <S.RecordContent data-testid="preassessmentContent">
        {preasessment &&
          preasessment.map(item => (
            <S.Record key={item.id}>
              <S.RecordTitle>
                <FormattedMessage id={item.statement} />
              </S.RecordTitle>
              <p>
                {item?.answers?.map(answer => (
                  <FormattedMessage key={answer.id} id={answer.value} />
                ))}
              </p>
            </S.Record>
          ))}
      </S.RecordContent>
    );
  };

  useEffect(() => {
    if (userInfoIsSuccess && !userInfo.care_profile) {
      preAssessmentsV1Refetch();
    }
    if (userInfoIsSuccess && userInfo.care_profile) {
      preAssessmentsV2Refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  useEffect(() => {
    paginatePreAssessments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preAssessment, pageNumber]);

  return (
    <S.UserInfoContainer>
      <Loading isLoading={getUserInfoRequest.isLoading()} />
      <HeaderUserInfo user={userInfo} />
      <S.Divisor />
      <S.TabsContainer>
        <TabList selectedIndex={2} />
        <S.PreAssessmentCard>
          <Card.Header>
            <Text.H5 data-testid="patientFileTitle">
              <FormattedMessage id="PREASSESSMENT.PATIENT.FILE" />
            </Text.H5>
          </Card.Header>
          <S.RecordContent data-testid="patientFileContent">
            {patientFileAnswers && (
              <>
                <S.Record>
                  <S.RecordTitle>
                    <FormattedMessage id="PREASSESSMENT.FIRST.QUESTION" />
                  </S.RecordTitle>
                  <p>{patientFileAnswers.question1}</p>
                </S.Record>
                <S.Record>
                  <S.RecordTitle>
                    <FormattedMessage id="PREASSESSMENT.SECOND.QUESTION" />
                  </S.RecordTitle>
                  <p>{patientFileAnswers.question2}</p>
                </S.Record>
                <S.Record>
                  <S.RecordTitle>
                    <FormattedMessage id="PREASSESSMENT.THIRD.QUESTION" />
                  </S.RecordTitle>
                  <p>{patientFileAnswers.question3}</p>
                </S.Record>
                <S.Record>
                  <S.RecordTitle>
                    <FormattedMessage id="PREASSESSMENT.FOURTH.QUESTION" />
                  </S.RecordTitle>
                  <p>
                    {intl.formatMessage({
                      id: `PREASSESSMENT.FOURTH.ANSWER.${patientFileAnswers.question4.toUpperCase()}`,
                      defaultMessage: ' ',
                    })}
                  </p>
                </S.Record>
              </>
            )}
          </S.RecordContent>
        </S.PreAssessmentCard>

        <S.PreAssessmentCard>
          <Card.Header>
            <Text.H5 data-testid="preassessmentTitle">
              <FormattedMessage id="PREASSESSMENT.PREASSESSMENT.CARD_TITLE" />
            </Text.H5>
          </Card.Header>

          {!userInfo.care_profile
            ? listPreassessmentV1(
                paginatedPreAssessment as PreAssessmentAnswerV1[],
              )
            : listPreassessmentV2(
                paginatedPreAssessment as PreAssessmentAnswerV2[],
              )}

          <S.PaginationControlWrapper>
            <Paginator
              startPage={pageNumber}
              lastPage={totalPages}
              next={handleNextPage}
              previous={handlePreviousPage}
            />
          </S.PaginationControlWrapper>
        </S.PreAssessmentCard>
      </S.TabsContainer>
    </S.UserInfoContainer>
  );
};

export default withAuth(PreAssessment);
